import React, {
  useCallback,
  useEffect,
  useState,
} from 'react'

import PropTypes from 'prop-types'

import RadioGroupContext from './context'


export default function RadioGroup({ handleChange, name, value, children, disabled, ...props }) {
  const [selectedValue, setSelectedValue] = useState(value)

  // If something outside of this component causes the value to change
  // (e.g., a larger clickable wrapper around the RadioButton), we
  // need to make sure that we update the selected value. If we don't
  // do this, then the initial value of `value` with which we call
  // `useState()` above will remain until `handleRadioChange()` is
  // invoked.
  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleRadioChange = useCallback(({ value: newValue, checked }) => {
    handleChange({
      checked,
      name,
      value: newValue,
    })

    setSelectedValue(newValue)
  }, [handleChange, name])

  // There's really no sense in memoizing this value because it's going
  // to change no matter what happens to get modified.
  const contextValue = {
    name,
    selectedValue,
    groupDisabled: disabled,
    handleChange: handleRadioChange,
  }

  return (
    <RadioGroupContext.Provider
      value={ contextValue }
    >
      <div
        role="radiogroup"
        { ...props }
      >
        { children }
      </div>
    </RadioGroupContext.Provider>
  )
}

RadioGroup.propTypes = {
  handleChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]),

  children: PropTypes.node,
  disabled: PropTypes.bool,
}
